/**
 * Typed mirror of `apps/shared/factsetConstants.js`.
 *
 * The server-side CJS module is the source of truth. The parity test
 * `apps/server/__tests__/factsetConstants.parity.test.mjs` reads both
 * files and fails the build if any literal here drifts from its server
 * counterpart, so we get both:
 *   - typed `as const` literals on the client (narrow types in unions,
 *     autocomplete in IDEs, no string-typo class of bugs), and
 *   - a single audited source of truth that operators / SREs can grep.
 *
 * If you add a new literal here, add it on the server side too. The
 * parity test will refuse to merge otherwise.
 */

export const FACTSET_SCOPES = {
  CONVERSATIONAL: "api|conversational",
  TEXT_TO_FORMULA: "api|formula/text-to-formula",
  OFFICE_TOOLS: "api|office-tools",
  OFFICE_REFRESH: "api|office-refresh",
  ID_LOOKUP: "api|content/idlookup",
  ENTITY: "api|content/entity",
} as const;
export type FactSetScope = (typeof FACTSET_SCOPES)[keyof typeof FACTSET_SCOPES];

/**
 * All `error:` codes the FactSet OAuth surface returns. The client maps a
 * subset of these (see `REAUTH_REQUIRED_OAUTH_ERROR_CODES`) to a
 * "Reconnect FactSet" CTA.
 */
export const FACTSET_OAUTH_ERROR_CODES = {
  NOT_CONNECTED: "factset_oauth_not_connected",
  EXPIRED: "factset_oauth_expired",
  REFRESH_FAILED: "factset_oauth_refresh_failed",
  PERSIST_FAILED: "factset_oauth_persist_failed",
  NOT_CONFIGURED: "factset_oauth_not_configured",
  UNSUPPORTED_MODE: "factset_oauth_unsupported_mode",
  SCOPE_MISSING: "factset_oauth_scope_missing",
  INVALID_STATE: "factset_oauth_invalid_state",
  STATE_EXPIRED: "factset_oauth_state_expired",
  MISSING_CODE: "factset_oauth_missing_code",
  MISSING_VERIFIER: "factset_oauth_missing_verifier",
  SESSION_TOO_LARGE: "factset_oauth_session_too_large",
  START_FAILED: "factset_oauth_start_failed",
  TOKEN_EXCHANGE_FAILED: "factset_oauth_token_exchange_failed",
  DISCONNECT_FAILED: "factset_oauth_disconnect_failed",
  EXCHANGE_NO_SESSION: "factset_oauth_exchange_no_session",
} as const;
export type FactSetOAuthErrorCode =
  (typeof FACTSET_OAUTH_ERROR_CODES)[keyof typeof FACTSET_OAUTH_ERROR_CODES];

/**
 * Subset of OAuth error codes that mean "user must reconnect FactSet".
 * Anything in this set drives the reauth CTA; other codes fall through
 * to generic failure messages.
 */
export const REAUTH_REQUIRED_OAUTH_ERROR_CODES = new Set<FactSetOAuthErrorCode>([
  FACTSET_OAUTH_ERROR_CODES.NOT_CONNECTED,
  FACTSET_OAUTH_ERROR_CODES.EXPIRED,
  FACTSET_OAUTH_ERROR_CODES.REFRESH_FAILED,
  FACTSET_OAUTH_ERROR_CODES.SCOPE_MISSING,
]);

export const FACTSET_TTF_ERROR_CODES = {
  MISSING_QUERY: "factset_ttf_missing_query",
  QUERY_TOO_LARGE: "factset_ttf_query_too_large",
  MISSING_JOB_ID: "factset_ttf_missing_job_id",
  NETWORK_ERROR: "factset_ttf_network_error",
  UPSTREAM_ERROR: "factset_ttf_upstream_error",
  INVALID_RESPONSE: "factset_ttf_invalid_response",
} as const;
export type FactSetTtfErrorCode =
  (typeof FACTSET_TTF_ERROR_CODES)[keyof typeof FACTSET_TTF_ERROR_CODES];

export const FACTSET_CHART_CREATOR_ERROR_CODES = {
  MISSING_PAYLOAD: "factset_chart_creator_missing_payload",
  NETWORK_ERROR: "factset_chart_creator_network_error",
  UPSTREAM_ERROR: "factset_chart_creator_upstream_error",
  INVALID_RESPONSE: "factset_chart_creator_invalid_response",
  REQUEST_TOO_LARGE: "factset_chart_creator_request_too_large",
  RESPONSE_TOO_LARGE: "factset_chart_creator_response_too_large",
  TIMEOUT: "factset_chart_creator_timeout",
} as const;
export type FactSetChartCreatorErrorCode =
  (typeof FACTSET_CHART_CREATOR_ERROR_CODES)[keyof typeof FACTSET_CHART_CREATOR_ERROR_CODES];

export const FACTSET_ID_LOOKUP_ERROR_CODES = {
  INVALID_REQUEST: "factset_id_lookup_invalid_request",
  MISSING_SEARCH_TEXT: "factset_id_lookup_missing_search_text",
  SEARCH_TEXT_TOO_LARGE: "factset_id_lookup_search_text_too_large",
  NETWORK_ERROR: "factset_id_lookup_network_error",
  TIMEOUT: "factset_id_lookup_timeout",
  RATE_LIMITED: "factset_id_lookup_rate_limited",
  FORBIDDEN: "factset_id_lookup_forbidden",
  UPSTREAM_ERROR: "factset_id_lookup_upstream_error",
  INVALID_RESPONSE: "factset_id_lookup_invalid_response",
} as const;
export type FactSetIdLookupErrorCode =
  (typeof FACTSET_ID_LOOKUP_ERROR_CODES)[keyof typeof FACTSET_ID_LOOKUP_ERROR_CODES];

export const FACTSET_SERVICE_IDS = {
  OAUTH: "factset_oauth",
  TTF: "factset_ttf",
  CHART_CREATOR: "factset_chart_creator",
  ID_LOOKUP: "factset_id_lookup",
} as const;

/**
 * Mounted route paths exposed by the add-in server. Using these
 * constants keeps the client from carrying a typo'd URL that the server
 * would 404 silently.
 */
export const FACTSET_ROUTES = {
  OAUTH_BASE: "/api/auth/factset/oauth",
  OAUTH_START: "/api/auth/factset/oauth/start",
  OAUTH_SESSION: "/api/auth/factset/oauth/session",
  OAUTH_CALLBACK: "/api/auth/factset/oauth/callback",
  OAUTH_EXCHANGE: "/api/auth/factset/oauth/exchange",
  OAUTH_DISCONNECT: "/api/auth/factset/oauth/disconnect",
  TTF_BASE: "/api/factset",
  TTF_GENERATE: "/api/factset/ttf/generate",
  TTF_RESULT: "/api/factset/ttf/result",
  CHART_CREATOR_PREVIEW: "/api/factset/chart-creator/preview-chart",
  CHART_CREATOR_DOWNLOAD_EXCEL: "/api/factset/chart-creator/download-excel-chart",
  CHART_CREATOR_STYLESETS: "/api/factset/chart-creator/stylesets",
  COMPANY_LOOKUP: "/api/factset/company-lookup",
} as const;

/** FactSet Chart Creator upstream paths, required headers, and workbook MIME type. */
export const FACTSET_CHART_CREATOR_API = {
  PREVIEW_CHART_PATH: "/office-tools/chart-creator/v0/preview-chart",
  DOWNLOAD_EXCEL_CHART_PATH: "/office-tools/chart-creator/v0/download-excel-chart",
  STYLESETS_PATH: "/office-tools/chart-creator/v0/stylesets",
  CHAIN_ID_HEADER: "X-Fds-Chain-Id",
  ORIGIN_PRODUCT_ID_HEADER: "X-FactSet-Origin-Product-Id",
  ORIGIN_PRODUCT_ID_VALUE: "38970",
  XLSX_CONTENT_TYPE: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
} as const;

/**
 * Values for the `status:` field on the server's TTF poll envelope.
 * The client treats anything other than these two as a bug-class
 * server error rather than continuing to poll forever.
 */
export const FACTSET_TTF_STATUS = {
  OK: "ok",
  PENDING: "pending",
} as const;
export type FactSetTtfStatus = (typeof FACTSET_TTF_STATUS)[keyof typeof FACTSET_TTF_STATUS];

/**
 * Maximum byte length the server accepts for a single TTF query body.
 * Mirrored on the client so the panel can surface a friendly error
 * before sending an oversize request over the wire.
 */
export const FACTSET_TTF_QUERY_MAX_BYTES = 4 * 1024;

/**
 * Maximum JSON body byte length the server will buffer for the TTF
 * generate route. Slightly larger than `FACTSET_TTF_QUERY_MAX_BYTES` to
 * leave room for the JSON envelope. Mirrored from
 * `apps/shared/factsetConstants.js` to keep the parity test in lockstep;
 * the client does not enforce it directly.
 *
 * The arithmetic is spelled out as literals rather than referencing
 * `FACTSET_TTF_QUERY_MAX_BYTES` so the parity-test parser, which only
 * understands numeric literal expressions, can compute the value. If you
 * bump either limit, bump the other in lockstep; the parity test will
 * fail loudly otherwise.
 */
export const FACTSET_TTF_REQUEST_MAX_BYTES = 4 * 1024 + 1024;

export const FACTSET_CHART_CREATOR_REQUEST_MAX_BYTES = 64 * 1024;
export const FACTSET_CHART_CREATOR_RESPONSE_MAX_BYTES = 32 * 1024 * 1024;
/** Keep five seconds for the BFF to return inside the client's five-minute deadline. */
export const FACTSET_CHART_CREATOR_UPSTREAM_TIMEOUT_MS =
  5 * 60 * 1000 - 5_000;

export const FACTSET_ID_LOOKUP_SEARCH_TEXT_MAX_BYTES = 512;
export const FACTSET_ID_LOOKUP_REQUEST_MAX_BYTES = 8 * 1024;
export const FACTSET_ID_LOOKUP_DEFAULT_LIMIT = 5;
export const FACTSET_ID_LOOKUP_MIN_LIMIT = 1;
/**
 * FactSet returns ten rows per dataset and the lookup payload carries no limit
 * parameter, so the cap is trimmed locally. Accepting a larger value would
 * promise candidates that cannot arrive. Raise this if FactSet exposes a limit.
 */
export const FACTSET_ID_LOOKUP_MAX_LIMIT = 10;
export const FACTSET_ID_LOOKUP_TOTAL_TIMEOUT_MS = 15 * 1000;
export const FACTSET_ID_LOOKUP_MAX_ATTEMPTS = 2;
export const FACTSET_ID_LOOKUP_MAX_RETRY_DELAY_MS = 1000;

/** Browser deadline includes a small allowance for the local proxy response. */
export const FACTSET_ID_LOOKUP_CLIENT_TIMEOUT_MS =
  FACTSET_ID_LOOKUP_TOTAL_TIMEOUT_MS + 2_000;

/**
 * Schema version stamped on every persisted `factsetOauth` blob. Bumped
 * server-side when the stored shape changes; the client mostly ignores
 * this today but mirroring it keeps the two in lockstep.
 */
export const FACTSET_OAUTH_SCHEMA_VERSION = 1;

/**
 * Hard ceiling on a single TTF round trip (submit + poll) when called
 * from inside the Excel AI cell-write loop. When exceeded, the caller
 * aborts the in-flight TTF call via `AbortController` and silently
 * falls back to the LLM-generated `=FDS(...)` formula carried alongside
 * the `ttfQuery` on the same `CellModification`.
 *
 * The cell-write path passes this as an explicit `runTtfQuery` budget (so it
 * is independent from the dev-panel default poll budget). It gives TTF a fair
 * chance to resolve while a single slow cell still cannot dominate a
 * multi-cell apply indefinitely.
 *
 * Client-only — server-side TTF requests are governed by the upstream
 * fetch/connect timeouts in `apps/server/routes/factsetApi.js`.
 */
export const EXCEL_TTF_PER_CALL_TIMEOUT_MS = 30_000;

/**
 * Maximum TTF query entries accepted in one `generate_formula` batch. The
 * actual outbound request concurrency is throttled separately inside the TTF
 * dispatcher so large batches are processed steadily instead of fanning out
 * every submit/poll cycle at once.
 */
export const TTF_MAX_CONCURRENT_QUERIES = 50;
